import { useCallback, useEffect, useState } from 'react';
import {
  createBanepadRegistration,
  listBanepadRegistrations,
  revokeBanepadDevice,
} from '../lib/banepadDevices';
import { findRegistrationForCourt } from '../engine/banepadDevice';
import {
  padPresence,
  readPadHeartbeats,
  type PadHeartbeat,
  type PadPresence,
} from '../lib/banepadHeartbeat';
import type { BanepadDeviceRegistration } from '../engine/banepadDevice';

// Central re-reads the persisted registrations on this cadence so a pad that
// pairs on its OWN device shows up without a manual refresh. Pairing itself
// happens on the physical pad route, not here (see /pad → BanepadDeviceRoute).
const DEVICE_REFRESH_MS = 5000;
// Heartbeats change every 30 s on the pads; polling them at 15 s keeps the
// «frakoblet» indicator within a minute of the truth without hammering Storage.
const HEARTBEAT_REFRESH_MS = 15_000;

// ─────────────────────────────────────────────────────────────────────
// useBanepadDevices — React glue for the Banepad pad-pairing PoC.
//
// Unlike the StoreProvider-composed slices, this is a SELF-CONTAINED hook used
// directly by the Banepad central view: the pairing feature is confined to that
// view, so its state does not need to be threaded through the global store /
// atomic mount loader. It owns the in-memory mirror of the persisted
// registrations and re-reads after every mutation so the rutenett cards reflect
// the latest pairing status.
//
// All persistence + token logic lives behind src/lib/banepadDevices.ts (the
// replaceable boundary) and src/engine/banepadDevice.ts (pure) — this hook only
// wires those into the component lifecycle.
// ─────────────────────────────────────────────────────────────────────
export function useBanepadDevices() {
  const [registrations, setRegistrations] = useState<BanepadDeviceRegistration[]>([]);
  const [heartbeats, setHeartbeats] = useState<Record<string, PadHeartbeat>>({});

  const refresh = useCallback(async (): Promise<BanepadDeviceRegistration[]> => {
    const list = await listBanepadRegistrations();
    setRegistrations(list);
    return list;
  }, []);

  // Load on mount, then keep in sync: pairing happens on a SEPARATE device (the
  // physical pad), so the central view must re-read periodically — and on window
  // focus — to reflect a newly-paired pad without a manual page refresh.
  useEffect(() => {
    void refresh();
    const id = setInterval(() => void refresh(), DEVICE_REFRESH_MS);
    const onFocus = () => void refresh();
    window.addEventListener('focus', onFocus);
    return () => {
      clearInterval(id);
      window.removeEventListener('focus', onFocus);
    };
  }, [refresh]);

  // Presence: poll the pads' heartbeats (separate keys, see lib/banepadHeartbeat).
  useEffect(() => {
    let cancelled = false;
    const poll = async () => {
      try {
        const hb = await readPadHeartbeats();
        if (!cancelled) setHeartbeats(hb);
      } catch {
        /* keep the last known heartbeats */
      }
    };
    void poll();
    const id = setInterval(() => void poll(), HEARTBEAT_REFRESH_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  const create = useCallback(
    async (
      courtId: number,
      courtName: string,
      opts: { force?: boolean } = {},
    ): Promise<BanepadDeviceRegistration> => {
      const reg = await createBanepadRegistration(courtId, courtName, opts);
      await refresh();
      return reg;
    },
    [refresh],
  );

  const revoke = useCallback(
    async (deviceId: string): Promise<BanepadDeviceRegistration | null> => {
      const reg = await revokeBanepadDevice(deviceId);
      await refresh();
      return reg;
    },
    [refresh],
  );

  /** Active (pending or paired) registration for a court, derived from local state. */
  const registrationForCourt = useCallback(
    (courtId: number | string): BanepadDeviceRegistration | null =>
      findRegistrationForCourt(registrations, courtId),
    [registrations],
  );

  /**
   * Presence of the pad paired to a court: online / offline (silent too long) /
   * unknown (paired but never heard from). Null when no pad is paired.
   */
  const presenceForCourt = useCallback(
    (
      courtId: number | string,
      now: number = Date.now(),
    ): { presence: PadPresence; silentMs: number; heartbeat: PadHeartbeat | null } | null => {
      const reg = findRegistrationForCourt(registrations, courtId);
      if (!reg || reg.status !== 'paired') return null;
      const hb = heartbeats[reg.id] ?? null;
      return { ...padPresence(hb, now), heartbeat: hb };
    },
    [registrations, heartbeats],
  );

  return {
    registrations,
    heartbeats,
    refresh,
    create,
    revoke,
    registrationForCourt,
    presenceForCourt,
  };
}
